import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "@common/utils/redline/ooxml/ooxml-names";
import { PROPOSED_REVISIONS_AUTHOR, ProposedRevisions } from "@common/utils/redline/ooxml/proposed-revisions";
import type { ChangeExcerptOptions } from "@common/utils/redline/text/change-excerpt";
import type { TableRowsExcerptOptions } from "@common/utils/redline/text/table-rows-excerpt";
import { type RedlinePayload, type RedlineTextProposal, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";

import { editLayerStampAfter, type LiveProposalState, type ProposalEditLayerInput } from "../../../utils/anchor/proposal/live-proposal";
import type { LiveProposalRequest } from "../../../utils/anchor/proposal/live-proposal-registry";
import { withRowDeletionsOf } from "../proposal-edit/editable-table-model";
import { textCardOfNewParagraphCard } from "../proposal-edit/new-paragraph-text-card";
import type { ProposalCardContent } from "./card-content";

export interface CardEditSource {
  textRedlineToEdit: RedlineTextProposal;
  textRedlineBeforeEdit: RedlineTextProposal | null;
  textRedlineWhenOpened: RedlineTextProposal | null;
  editAuthor: string;
}

export interface CardPreviewSource {
  markup: string;
  textRedline: RedlineTextProposal | null;
  bookmarks: string[];
  afterBookmarks: string[];
  edit: CardEditSource | null;
}

export interface CardLivePreview {
  bookmarkNames: string[];
  redlineOoxml: string | null;
  textRedline: RedlineTextProposal | null;
  editLayer: ProposalEditLayerInput | null;
  insertAfter: boolean;
}

export const CARD_EXCERPT_CONTEXT_UNITS = 4;

export const CARD_EXCERPT_BLOCK_LIMIT = 2;

function proposalShownUnder(source: CardPreviewSource, savedEdit: RedlineTextProposal | null): RedlineTextProposal | null {
  const proposalBeforeEdit = source.edit?.textRedlineBeforeEdit ?? null;
  if (proposalBeforeEdit === null) return source.textRedline ?? source.edit?.textRedlineToEdit ?? null;
  if (savedEdit === null) return proposalBeforeEdit;
  return withRowDeletionsOf(proposalBeforeEdit, savedEdit);
}

export function cardPreviewSourceOfContent(content: ProposalCardContent, textRedlineBeforeEdit: RedlineTextProposal | null, textRedlineWhenOpened: RedlineTextProposal | null, editAuthor: string): CardPreviewSource | null {
  if (content.kind === "comment" || content.kind === "reply") return null;
  const textRedlineToEdit = content.textRedline ?? (content.kind === "paragraph" ? textCardOfNewParagraphCard(content, editAuthor) : null);
  return {
    markup: content.markup ?? "",
    textRedline: content.textRedline,
    bookmarks: content.kind === "paragraph" ? content.bookmarks ?? [content.precedingParagraphBookmark] : content.bookmarks,
    afterBookmarks: content.afterBookmarks ?? [],
    edit: textRedlineToEdit === null ? null : { textRedlineToEdit, textRedlineBeforeEdit, textRedlineWhenOpened, editAuthor },
  };
}

export function cardLivePreviewOf(source: CardPreviewSource): CardLivePreview {
  const savedEdit = source.edit !== null && source.edit.textRedlineBeforeEdit !== null ? source.textRedline : null;
  const shownTextRedline = proposalShownUnder(source, savedEdit);
  const previewsMarkup = shownTextRedline === null && source.markup.length > 0;
  return {
    bookmarkNames: source.bookmarks,
    redlineOoxml: previewsMarkup ? source.markup : null,
    textRedline: shownTextRedline,
    editLayer: savedEdit === null || source.edit === null ? null : { textRedline: savedEdit, author: source.edit.editAuthor, textRedlineWhenOpened: source.edit.textRedlineWhenOpened },
    insertAfter: shownTextRedline === null && source.afterBookmarks.length > 0,
  };
}

export function redlinePayloadOf(preview: Pick<CardLivePreview, "redlineOoxml" | "textRedline">, signedInAuthorName: string | null): RedlinePayload | null {
  if (preview.textRedline !== null) return textsPayloadFrom(preview.textRedline);
  if (preview.redlineOoxml !== null && preview.redlineOoxml.length > 0 && signedInAuthorName !== null) return { kind: "ooxml", redlineOoxml: preview.redlineOoxml, author: signedInAuthorName };
  return null;
}

export function liveProposalRequestOf(preview: CardLivePreview, signedInAuthorName: string | null): LiveProposalRequest | null {
  const redline = redlinePayloadOf(preview, signedInAuthorName);
  if (redline === null || preview.insertAfter || preview.bookmarkNames.length === 0 || signedInAuthorName === null) return null;
  return { bookmarkNames: preview.bookmarkNames, redline, editLayer: preview.editLayer };
}

export function liveProposalRequestOfCard(content: ProposalCardContent, textRedlineBeforeEdit: RedlineTextProposal | null, textRedlineWhenOpened: RedlineTextProposal | null, signedInAuthorName: string): LiveProposalRequest | null {
  const source = cardPreviewSourceOfContent(content, textRedlineBeforeEdit, textRedlineWhenOpened, signedInAuthorName);
  return source === null ? null : liveProposalRequestOf(cardLivePreviewOf(source), signedInAuthorName);
}

function sitsInTableCell(paragraph: Element): boolean {
  for (let ancestor = paragraph.parentElement; ancestor !== null; ancestor = ancestor.parentElement) {
    if (ancestor.namespaceURI === WORDPROCESSINGML_NS && elementNameWithoutPrefix(ancestor) === "tc") return true;
  }
  return false;
}

function withoutUnchangedAnchorParagraphs(previewPackageOoxml: string, textRedline: RedlineTextProposal): string {
  if (textRedline.insertions.length === 0) return previewPackageOoxml;
  const unchangedAnchorBookmarks = textRedline.paragraphs
    .filter((paragraph) => paragraph.textProposed === paragraph.textWhenProposed && paragraph.bookmark !== null)
    .map((paragraph) => paragraph.bookmark as string);
  if (unchangedAnchorBookmarks.length === 0) return previewPackageOoxml;
  const previewRange = new OoxmlRange(previewPackageOoxml);
  const paragraphByBookmark = previewRange.paragraphElementsByBookmarkName();
  const unchangedAnchorParagraphs = new Set(
    unchangedAnchorBookmarks
      .map((bookmark) => paragraphByBookmark.get(bookmark))
      .filter((paragraph): paragraph is Element => paragraph !== undefined && !sitsInTableCell(paragraph)),
  );
  if (unchangedAnchorParagraphs.size === 0) return previewPackageOoxml;
  const shownParagraphXmls = Array.from({ length: previewRange.paragraphCount }, (_, index) => index)
    .filter((index) => !unchangedAnchorParagraphs.has(previewRange.paragraphElement(index)))
    .map((index) => previewRange.paragraphXml(index));
  return shownParagraphXmls.length === 0 ? previewPackageOoxml : OoxmlRange.packageAround(shownParagraphXmls.join(""));
}

export function shownPreviewPackageOf(previewPackageOoxml: string | null, stampedAt: string | null, textRedlineShown: RedlineTextProposal | null): string | null {
  if (!previewPackageOoxml) return previewPackageOoxml;
  const shownPackage = textRedlineShown === null ? previewPackageOoxml : withoutUnchangedAnchorParagraphs(previewPackageOoxml, textRedlineShown);
  return stampedAt ? ProposedRevisions.relabel({ packageOoxml: shownPackage, stampedAt }) : shownPackage;
}

export function cardExcerptOptionsOf(state: Pick<LiveProposalState, "stampedAt">, preview: Pick<CardLivePreview, "editLayer">, blockLimit: number | null): ChangeExcerptOptions {
  const editStamps = preview.editLayer !== null && state.stampedAt !== null ? [editLayerStampAfter(state.stampedAt)] : [];
  return { contextUnits: CARD_EXCERPT_CONTEXT_UNITS, changeAuthors: [PROPOSED_REVISIONS_AUTHOR], changeStamps: editStamps, countsMovesAsChanges: true, blockLimit };
}

export const COLLAPSED_TABLE_ROW_BUDGET = 4;

export function collapsedTableRowsOptionsOf(state: Pick<LiveProposalState, "stampedAt">, preview: Pick<CardLivePreview, "editLayer">): TableRowsExcerptOptions {
  const { changeAuthors, changeStamps, countsMovesAsChanges } = cardExcerptOptionsOf(state, preview, null);
  return { changeAuthors, changeStamps, countsMovesAsChanges, rowBudget: COLLAPSED_TABLE_ROW_BUDGET };
}
